import { Box, useTheme } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import { PointTooltipProps, ResponsiveLine } from '@nivo/line';
import { TableTooltip } from '@nivo/tooltip';
import moment from 'moment';
import React from 'react';

import * as GQL from '~/graphql/generated/client';
import Card from '~c/components/base/card';
import { Color } from '~c/components/base/core';
import Text from '~c/components/base/text';
import { useWindowSize } from '~c/hooks/use-hooks';

const MIN_GRAPH_HEIGHT = 300;

// Risk level → Y value
const RISK_LINE_VALUE: Record<string, number> = {
  [GQL.AssessmentRiskStatusType.Unavailable]: 0,
  [GQL.AssessmentRiskStatusType.Low]: 1,
  [GQL.AssessmentRiskStatusType.Medium]: 2,
  [GQL.AssessmentRiskStatusType.High]: 3,
};

const SERIES_KEYS = [
  'Residual Risk',
  'Technology Risk',
  'Alignment Risk',
] as const;
type SeriesKey = (typeof SERIES_KEYS)[number];

const SERIES_COLORS: Record<SeriesKey, string> = {
  'Residual Risk': Color.deepDown,
  'Technology Risk': '#7B5EA7',
  'Alignment Risk': '#2E9E86',
};

const COMPLIANCE_LEVEL_COLORS: Record<number, string> = {
  0: Color.lightGrey,
  1: '#19C3D6',
  2: Color.riskMediumAmber,
  3: Color.red,
};

function complianceLabel(value: number): string {
  if (value === 0) {
    return 'Unavailable';
  }
  if (value === 1) {
    return 'Low';
  }
  if (value === 2) {
    return 'Medium';
  }
  if (value === 3) {
    return 'High';
  }
  return '';
}

type Snapshot = Pick<
  GQL.RiskStatusSnapshot,
  'residualRisk' | 'technologyRisk' | 'alignmentRisk' | 'timestamp'
>;

interface Props {
  className?: string;
  riskHistory: Snapshot[];
}

type SeriesData = {
  id: SeriesKey;
  color: string;
  data: {
    x: string;
    y: number;
    timestampMs: number;
    rawStatus: string;
  }[];
};

// Constant vertical offset per series so overlapping lines stay visually separated.
// Tooltip uses rawStatus, so the offset is purely visual.
const SERIES_Y_OFFSET: Record<SeriesKey, number> = {
  'Residual Risk': 0,
  'Technology Risk': 0.08,
  'Alignment Risk': -0.08,
};

function buildRiskSeriesData(riskHistory: Snapshot[]): SeriesData[] {
  const sorted = [...riskHistory].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const mapSeries = (
    id: SeriesKey,
    pick: (s: Snapshot) => GQL.AssessmentRiskStatusType
  ): SeriesData => ({
    id,
    color: SERIES_COLORS[id],
    data: sorted.map((s) => {
      const status = pick(s);
      const baseY = RISK_LINE_VALUE[status] ?? 0;
      return {
        x: moment(s.timestamp).format('MMM D'),
        y: baseY + SERIES_Y_OFFSET[id],
        timestampMs: new Date(s.timestamp).getTime(),
        rawStatus: status,
      };
    }),
  });

  return [
    mapSeries('Residual Risk', (s) => s.residualRisk),
    mapSeries('Technology Risk', (s) => s.technologyRisk),
    mapSeries('Alignment Risk', (s) => s.alignmentRisk),
  ];
}

const RiskHistoryTooltip: React.FC<PointTooltipProps> = ({ point }) => {
  const ts = (point.data as { timestampMs?: number }).timestampMs;
  const date = ts ? moment(ts).format('MMMM Do YYYY, h:mm:ss A') : '';
  const seriesId = point.serieId as SeriesKey;
  const raw =
    (point.data as { rawStatus?: string }).rawStatus ??
    complianceLabel(point.data.y as number);
  const color = SERIES_COLORS[seriesId] ?? point.serieColor;

  return (
    <TableTooltip
      title={
        <Text sz="body16" sx={{ padding: '10px 0px' }}>
          {date}
        </Text>
      }
      rows={[
        [
          <span
            key="dot"
            style={{
              display: 'block',
              width: '12px',
              height: '12px',
              background: color,
              borderRadius: '50%',
            }}
          />,
          <Text key="name" sz="body14" sx={{ padding: '3px 0px' }}>
            {seriesId}
          </Text>,
          <strong key="val">{raw}</strong>,
        ],
      ]}
    />
  );
};

const RiskHistoryGraphImpl: React.FC<Props> = ({ className, riskHistory }) => {
  const theme = useTheme();
  const { height } = useWindowSize();
  const graphHeight = Math.max(MIN_GRAPH_HEIGHT, height / 5);
  const axisColor = theme.palette.text.primary;
  const tooltipBg = theme.palette.background.paper;

  const riskSeriesData = React.useMemo(
    () => buildRiskSeriesData(riskHistory),
    [riskHistory]
  );

  if (!riskHistory || riskHistory.length === 0) {
    return null;
  }

  const nivoTheme = {
    axis: {
      ticks: { text: { fontSize: 14, fill: axisColor } },
    },
    tooltip: {
      table: { background: tooltipBg },
      container: { background: tooltipBg },
    },
  };

  return (
    <Box className={className}>
      <Card title="Risk History">
        <Box sx={{ height: graphHeight }}>
          <ResponsiveLine
            data={riskSeriesData}
            margin={{ top: 50, right: 20, bottom: 50, left: 110 }}
            theme={nivoTheme}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: -0.2, max: 3.2 }}
            curve="linear"
            lineWidth={3}
            axisTop={null}
            axisRight={null}
            axisBottom={{ tickPadding: 15 }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 10,
              tickRotation: 0,
              tickValues: [0, 1, 2, 3],
              format: (value) => complianceLabel(value as number),
            }}
            colors={{ datum: 'color' }}
            enablePoints={false}
            useMesh
            enableGridY={false}
            markers={[0, 1, 2, 3].map((value) => ({
              axis: 'y' as const,
              legendOrientation: 'vertical' as const,
              lineStyle: {
                stroke: COMPLIANCE_LEVEL_COLORS[value] ?? axisColor,
                strokeWidth: 0.5,
                strokeDasharray: value === 0 ? '4 4' : 'none',
              },
              value,
            }))}
            tooltip={RiskHistoryTooltip}
            legends={[
              {
                anchor: 'top-right',
                direction: 'row',
                translateY: -35,
                itemsSpacing: 12,
                itemWidth: 130,
                itemHeight: 18,
                symbolShape: 'circle',
                symbolSize: 10,
                itemTextColor: axisColor,
                toggleSerie: true,
              },
            ]}
          />
        </Box>
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {[
              {
                label: 'Unavailable',
                desc: 'No assessments started',
                color: COMPLIANCE_LEVEL_COLORS[0],
              },
              {
                label: 'Low',
                desc: 'Fully compliant',
                color: COMPLIANCE_LEVEL_COLORS[1],
              },
              {
                label: 'Medium',
                desc: 'Partially compliant',
                color: COMPLIANCE_LEVEL_COLORS[2],
              },
              {
                label: 'High',
                desc: 'Non-compliant',
                color: COMPLIANCE_LEVEL_COLORS[3],
              },
            ].map((item) => (
              <Text
                key={item.label}
                sz="body12"
                sx={{ fontSize: '10px', color: 'text.disabled' }}
              >
                <strong style={{ color: item.color }}>{item.label}</strong>
                {' — '}
                {item.desc}
              </Text>
            ))}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default muiStyled(RiskHistoryGraphImpl)(() => {
  return css``;
});
